import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { axiosInstance } from '../../../axiosApi';
import { formatPublicCount, shouldShowPublicUsage } from './publicCount';
import { publicText } from './publicColors';

const Section = styled.section`
  margin-top: 1.5rem;
  color: ${publicText};
  font-size: 0.95rem;
`;

const Figures = styled.p`
  margin: 0 0 0.35rem;
  font-weight: 600;
`;

const AsOf = styled.p`
  margin: 0;
  color: ${publicText};
  opacity: 0.7;
`;

type PublicUsage = {
  account_count: number;
  document_count: number;
  as_of: string;
};

/**
 * Live counts only. Hidden when the API fails, or until 20 accounts exist, so the page
 * never shows a made-up number. Once shown, both figures are rounded down and marked with +.
 * Avatars are omitted: no one has consented to publish a photo.
 */
const UsageCounts = (): JSX.Element | null => {
  const [usage, setUsage] = useState<PublicUsage | null>(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get('/public/usage/')
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        if (
          typeof data?.account_count !== 'number' ||
          typeof data?.document_count !== 'number' ||
          typeof data?.as_of !== 'string'
        ) {
          return;
        }
        setUsage({
          account_count: data.account_count,
          document_count: data.document_count,
          as_of: data.as_of,
        });
      })
      .catch(() => {
        /* leave hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!usage || !shouldShowPublicUsage(usage.account_count)) return null;

  const asOfDate = usage.as_of.slice(0, 10);

  return (
    <Section aria-label="Usage">
      <Figures>
        {formatPublicCount(usage.account_count, 'accounts')}
        {' · '}
        {formatPublicCount(usage.document_count, 'documents stored')}
      </Figures>
      <AsOf>
        As of <time dateTime={usage.as_of}>{asOfDate}</time>
      </AsOf>
    </Section>
  );
};

export default UsageCounts;
